import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { Helmet } from 'react-helmet-async';
import { Shield, ChevronDown, Check, LogIn, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsentPage({ slug }) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [doc, setDoc] = useState(null);
  const [myAcceptance, setMyAcceptance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({});

  const loadDoc = useCallback(async () => {
    const r = await fetch(`${API}/public/consent/${slug}?lang=${i18n.language}`);
    if (r.ok) setDoc(await r.json());
  }, [slug, i18n.language]);

  const loadMine = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/consent/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        const me = (d.consents || []).find(c => c.doc_slug === slug);
        setMyAcceptance(me || null);
      }
    } catch {/* ignore */}
  }, [slug, isAuthenticated, getAccessTokenSilently]);

  useEffect(() => { (async () => { await loadDoc(); await loadMine(); setLoading(false); })(); }, [loadDoc, loadMine]);

  async function accept() {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/consent/accept`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_slug: slug, version: doc.version }),
      });
      if (r.ok) {
        toast.success('Prihvaćeno');
        await loadMine();
      } else { toast.error('Greška'); }
    } finally { setSubmitting(false); }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>;
  if (!doc) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-gray-500">Dokument nije pronađen.</div>;

  const title = slug === 'politika-privatnosti' ? 'Politika privatnosti' : 'Pravila komentiranja';
  const acceptedCurrent = myAcceptance && myAcceptance.version === doc.version;
  const acceptedOlder = myAcceptance && myAcceptance.version !== doc.version;

  return (
    <>
      <Helmet><title>{`${title} — E-MINA`}</title></Helmet>
      <div className="max-w-3xl mx-auto px-4 py-10 pb-32" data-testid={`consent-page-${slug}`}>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
          <Shield size={14} /> Verzija {doc.version} • Stupa na snagu {doc.effective_from ? new Date(doc.effective_from).toLocaleDateString('hr') : '—'}
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">{title}</h1>

        {/* Summary banner */}
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600 rounded-r-lg p-5 mb-8" data-testid="consent-summary">
          <p className="text-xs uppercase tracking-wider font-bold text-red-700 dark:text-red-300 mb-1.5">Kratki sažetak</p>
          <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{doc.summary}</p>
        </div>

        {acceptedOlder && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-lg p-4 mb-6 text-sm text-amber-900 dark:text-amber-200" data-testid="older-version-notice">
            Nova verzija dostupna — molimo ponovno prihvatite.
          </div>
        )}

        {/* Accordion sections */}
        <div className="space-y-2" data-testid="consent-sections">
          {(doc.sections || []).map((sec) => {
            const open = expanded[sec.id];
            return (
              <div key={sec.id} className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden" data-testid={`consent-section-${sec.id}`}>
                <button onClick={() => setExpanded(e => ({ ...e, [sec.id]: !e[sec.id] }))}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800">
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">{sec.title}</span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="px-4 pb-4 prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: sec.body_html }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Sticky footer bar */}
        <div className="fixed bottom-0 inset-x-0 bg-white dark:bg-[#0f172a] border-t border-gray-200 dark:border-gray-800 shadow-lg z-40" data-testid="consent-sticky-bar">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="text-xs text-gray-600 dark:text-gray-300">
              Trenutna verzija: <strong>{doc.version}</strong>
              {acceptedCurrent && (
                <span className="ml-2 text-emerald-600 dark:text-emerald-400">• Prihvaćeno {new Date(myAcceptance.accepted_at).toLocaleDateString('hr')}</span>
              )}
              {!myAcceptance && isAuthenticated && <span className="ml-2 text-amber-600">• Nije prihvaćeno</span>}
              {!isAuthenticated && <span className="ml-2 text-gray-500">• Prijavite se za prihvaćanje</span>}
            </div>
            {acceptedCurrent ? (
              <button disabled className="px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded flex items-center gap-2" data-testid="consent-accepted-btn">
                <Check size={14} /> Prihvaćeno
              </button>
            ) : (
              <button onClick={accept} disabled={submitting}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded disabled:opacity-50 flex items-center gap-2" data-testid="consent-accept-btn">
                {submitting ? <Loader2 className="animate-spin" size={14} /> : (!isAuthenticated ? <LogIn size={14} /> : <Check size={14} />)}
                {!isAuthenticated ? 'Prijavi se i prihvati' : `Prihvati verziju ${doc.version}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
